/**
 * Project-specific patterns for dealing with focus-related accessibility functionality.
 */
export default class FocusUtil {

  /**
   * Should be called to ensure a focusable/tabbable element no longer has focus
   * after it has been clicked/selected (when appropriate).
   */
  static blurActiveElement() {
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }

  /**
   * Hooks up the project's "standard" handler for when `enter` (or space) is pressed on
   * focused element: It performs a click, and blurs the focus.
   *
   * Does not support removing the listener.
   */
  static setStandardEnterHandler($el, shouldNotBlur=false) {
    const handler = (e) => {
      if (FocusUtil.isEventKeyboardSelect(e)) {
        e.stopImmediatePropagation();
        e.preventDefault();
        document.activeElement.click();
        if (!shouldNotBlur) {
          FocusUtil.blurActiveElement();
        }
      }
    };
    $el.on("keydown", handler);
  }

  /**
   * Similar to `setStandardEnterHandler`, but does custom callback rather than
   * sending click event (Note how the callback gets passed the original event).
   *
   * Does not support removing the listener.
   */
  static setEnterHandler($el, callback) {
    const handler = (e) => {
      if (FocusUtil.isEventKeyboardSelect(e)) {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (callback) {
          callback(e);
        }
      }
    };
    $el.on("keydown", handler);
  }

  /**
   * Convenience function to set up the project's "standard treatment"
   * for actionable UI elements w/r/t focus.
   */
  static doStandardTreatment($el, shouldNotBlurAfterEnter=false) {
    if (!$el.attr("tabindex") && $el.attr("tabindex") !== 0) {
      cl("NB, element has no tabindex currently", $el);
    }
    // 1. Support enter and space key.
    FocusUtil.setStandardEnterHandler($el, shouldNotBlurAfterEnter);
    // 2. Make sure mouse or touch gesture does not leave unwanted residue on the focusable element.
    $el.on("mouseup touchend touchcancel", e => FocusUtil.blurActiveElement());
  }

  /**
   * Is the event a keydown event that can be interpreted as a "select" action
   * (ie, enter key or space key).
   */
  static isEventKeyboardSelect(e) {
    const keyCode = e.keyCode ? e.keyCode : e.which;
    const b = document.activeElement && (keyCode === 13 || keyCode === 32);
    return b;
  }
  
  /**
   * For development only.
   * Prints the current document active element whenever it changes.
   */
  static printActiveElementChanges() {
    setInterval(() => { // yea what
      if (document.activeElement !== FocusUtil.#currentActiveElement) {
        cl('active element:', document.activeElement);
      }
      FocusUtil.#currentActiveElement = document.activeElement;
    }, 200);
  }
  static #currentActiveElement;
}